import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, Search, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const PAGE_SIZE = 20;

const exportCSV = (rows) => {
  if (!rows.length) return;
  const mapped = rows.map((c) => ({
    id: c.id, date: c.created_at, prenom: c.first_name, nom: c.last_name,
    email: c.email, telephone: c.phone || "", ville: c.city || "",
    service: c.service_type || "", message: c.message || "",
  }));
  const headers = Object.keys(mapped[0]);
  const lines = [
    headers.join(";"),
    ...mapped.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(";")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "contacts-bionet.csv"; a.click();
  URL.revokeObjectURL(url);
};

const AdminContactsPage = () => {
  const nav = useNavigate();
  const { contacts, deleteContact } = useAdmin();
  const [search, setSearch] = useState("");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState(null);

  const filtered = useMemo(() => {
    const now = new Date();
    return contacts.filter((c) => {
      if (periodFilter !== "all") {
        const days = periodFilter === "7j" ? 7 : 30;
        if ((now - new Date(c.created_at)) / 86400000 > days) return false;
      }
      if (search) {
        const t = search.toLowerCase();
        const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
        if (!name.includes(t) && !(c.email || "").toLowerCase().includes(t) && !(c.city || "").toLowerCase().includes(t)) return false;
      }
      return true;
    });
  }, [contacts, search, periodFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => setPage(1);

  const handleDelete = async () => {
    if (!confirmId) return;
    await deleteContact(confirmId);
    setConfirmId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Nom, email, ville…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); reset(); }}
            className="pl-9"
            data-testid="contacts-search"
          />
        </div>
        <select
          value={periodFilter}
          onChange={(e) => { setPeriodFilter(e.target.value); reset(); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-green"
          data-testid="contacts-period-filter"
        >
          <option value="all">Toutes périodes</option>
          <option value="7j">7 derniers jours</option>
          <option value="30j">30 derniers jours</option>
        </select>
        <Button variant="outline" className="rounded-full text-sm ml-auto" onClick={() => exportCSV(filtered)} data-testid="export-contacts-csv">
          <Download className="h-4 w-4 mr-2" /> Exporter CSV
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left text-xs font-semibold uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Téléphone</th>
              <th className="px-4 py-3">Ville</th>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Message</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageItems.map((c) => (
              <tr
                key={c.id}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => nav(`/admin/contacts/${c.id}`)}
                data-testid={`contact-row-${c.id}`}
              >
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                  {new Date(c.created_at).toLocaleDateString("fr-FR")}
                </td>
                <td className="px-4 py-3 font-medium text-slate-900">{c.first_name} {c.last_name}</td>
                <td className="px-4 py-3 text-slate-600">{c.email}</td>
                <td className="px-4 py-3 text-slate-600">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{c.city || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{c.service_type || "—"}</td>
                <td className="px-4 py-3 text-slate-400 max-w-[200px] truncate">{c.message}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setConfirmId(c.id)}
                    className="text-slate-300 hover:text-red-500 transition-colors"
                    data-testid={`delete-contact-${c.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {pageItems.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">Aucun contact trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>{filtered.length} contacts -page {page}/{totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!confirmId} onOpenChange={(v) => !v && setConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ce contact ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Cette action est irréversible.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmId(null)}>Annuler</Button>
            <Button className="rounded-full bg-red-500 hover:bg-red-600 text-white" onClick={handleDelete} data-testid="confirm-delete-contact">
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContactsPage;
